import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useNotification } from "@/context/NotificationContext";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import {
  clearEnrollmentsWaitlistState,
  fetchEventEnrollmentsAction,
  fetchEventWaitlistItemsAction,
  fetchWaitlistDetailsAction,
  updateWaitlistPositionAction,
  moveWaitlistToEnrollmentAction,
  removeWaitlistItemAction,
  moveEnrollmentToWaitlistAction,
  fetchEventSessionAttendancesAction,
  fetchAttendanceGridAction,
  updateEventSessionAttendanceAction,
  bulkUpdateEventSessionAttendanceAction,
  exportEventAttendanceCsvAction,
  setSelectedAttendanceStatus as setSelectedAttendanceStatusAction,
  finalizeEventSessionAction,
  updateDayStatusOptimistically,
  bulkUpdateDayStatusesOptimistically,
  updateDayNotesOptimistically,
} from "@/modules/eventRegistration/features/EventWaitlist/slices/enrollmentsWaitlistSlice";
import { EventEnrollmentItemResponseDTO } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/types";
import { EventWaitlistItemResponse } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventWaitlistItems/types";
import { UpdateEventSessionAttendanceRequest } from "@/modules/eventRegistration/features/EventWaitlist/api/updateEventSessionAttendance/types";
import { downloadFile } from "@/utils/downloadFile";

export type RegisteredUser = EventEnrollmentItemResponseDTO;
export type WaitlistEntry = EventWaitlistItemResponse;

interface UseWaitlistProps {
  eventSessionUuid?: string;
}

export default function useWaitlist({
  eventSessionUuid,
}: UseWaitlistProps = {}) {
  const { toast } = useToast();
  const { showNotification } = useNotification();
  const dispatch = useDispatch<AppDispatch>();

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    enrollments,
    waitlistItems,
    waitlistDetails,
    errorUpdatePosition,
    attendances,
    loadingAttendances,
    loadingUpdateAttendance,
    errorUpdateAttendance,
    loadingExportCsv,
    selectedAttendanceStatus,
    attendanceGridItems,
    attendanceGridSessionDays,
    loadingAttendanceGrid,
    loadingEnrollments,
    loadingWaitlistItems,
  } = useSelector((state: RootState) => state.enrollmentsWaitlist);

  const validTabs = ["roster", "attendance", "billing"] as const;
  type Tab = (typeof validTabs)[number];
  const tabParam = searchParams.get("tab");
  const activeTab: Tab = validTabs.includes(tabParam as Tab)
    ? (tabParam as Tab)
    : "roster";

  const setActiveTab = useCallback(
    (tab: Tab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "roster") {
          next.delete("tab");
        } else {
          next.set("tab", tab);
        }
        return next;
      });
    },
    [setSearchParams],
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMovingToWaitlist, setIsMovingToWaitlist] = useState(false);
  const [isReplacing] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isChangingPosition, setIsChangingPosition] = useState(false);

  const setSelectedAttendanceStatusValue = (value: string | null) => {
    dispatch(setSelectedAttendanceStatusAction(value));
  };

  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showMoveToWaitlistDialog, setShowMoveToWaitlistDialog] =
    useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showFinalizeSessionDialog, setShowFinalizeSessionDialog] =
    useState(false);

  const [selectedUser, setSelectedUser] = useState<WaitlistEntry | null>(null);
  const [selectedRegistration, setSelectedRegistration] =
    useState<RegisteredUser | null>(null);

  useEffect(() => {
    if (!eventSessionUuid) return;

    (async () => {
      try {
        await Promise.all([
          dispatch(fetchEventEnrollmentsAction({ uuid: eventSessionUuid })),
          dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid })),
          dispatch(fetchWaitlistDetailsAction({ uuid: eventSessionUuid })),
          dispatch(
            fetchAttendanceGridAction({
              uuid: eventSessionUuid,
              status: undefined,
            }),
          ),
          dispatch(
            fetchEventSessionAttendancesAction({
              uuid: eventSessionUuid,
              status: undefined,
            }),
          ),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch event data. Please try again.",
          variant: "destructive",
        });
      }
    })();

    return () => {
      dispatch(clearEnrollmentsWaitlistState());
    };
  }, [eventSessionUuid, dispatch, toast]);

  useEffect(() => {
    if (!eventSessionUuid) return;

    (async () => {
      try {
        await dispatch(
          fetchAttendanceGridAction({
            uuid: eventSessionUuid,
            status: selectedAttendanceStatus || undefined,
          }),
        );
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        toast({
          title: "Error",
          description: "Failed to fetch attendance data. Please try again.",
          variant: "destructive",
        });
      }
    })();
  }, [selectedAttendanceStatus, eventSessionUuid, dispatch, toast]);

  const handleTabChange = (value: string) => {
    if (validTabs.includes(value as Tab)) {
      setActiveTab(value as Tab);
    }
  };

  const refreshData = async () => {
    if (!eventSessionUuid) return;
    setIsRefreshing(true);
    try {
      await dispatch(fetchEventEnrollmentsAction({ uuid: eventSessionUuid }));
      await dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid }));
      await dispatch(fetchWaitlistDetailsAction({ uuid: eventSessionUuid }));
      await dispatch(
        fetchAttendanceGridAction({
          uuid: eventSessionUuid,
          status: selectedAttendanceStatus || undefined,
        }),
      );
      await dispatch(
        fetchEventSessionAttendancesAction({
          uuid: eventSessionUuid,
          status: selectedAttendanceStatus || undefined,
        }),
      );
      toast({
        title: "Data refreshed",
        description: "The waitlist data has been refreshed.",
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast({
        title: "Error",
        description: "Failed to refresh data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const removeFromWaitlist = async (
    registrationId: number,
    removalReason?: string,
  ) => {
    if (!eventSessionUuid) return;
    setIsProcessing(true);
    try {
      await dispatch(
        removeWaitlistItemAction({
          uuid: eventSessionUuid,
          enrollmentId: registrationId,
          removalReason,
        }),
      );
      toast({
        title: "Removed from waitlist",
        description: "User has been removed from the waitlist.",
      });
      await refreshData();
    } catch (error) {
      console.error("Error removing from waitlist:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove waitlist item.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const moveToWaitlist = async (registration: RegisteredUser) => {
    console.log("moveToWaitlist called with registration:", registration);
  };

  const registerFromWaitlist = async (
    waitlistId: number,
    sendNotification: boolean = true,
  ) => {
    if (!eventSessionUuid) return;
    setIsRegistering(true);
    try {
      await dispatch(
        moveWaitlistToEnrollmentAction({
          uuid: eventSessionUuid,
          enrollmentId: waitlistId,
          sendNotification: sendNotification,
        }),
      );
      toast({
        title: "User registered",
        description:
          "User has been successfully moved from waitlist to enrollments.",
      });
      await refreshData();
    } catch (error) {
      console.error("Error moving waitlist to enrollment:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to move waitlist to enrollment.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const moveFromEnrollmentToWaitlist = async (
    enrollmentId: number,
    sendNotification: boolean = true,
  ) => {
    if (!eventSessionUuid) return;
    setIsMovingToWaitlist(true);
    try {
      await dispatch(
        moveEnrollmentToWaitlistAction({
          uuid: eventSessionUuid,
          enrollmentId: enrollmentId,
          sendNotification: sendNotification,
        }),
      );
      toast({
        title: "Moved to waitlist",
        description: "The user has been moved from enrollment to the waitlist.",
      });
      await refreshData();
    } catch (error) {
      console.error("Error moving enrollment to waitlist:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to move enrollment to waitlist.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsMovingToWaitlist(false);
    }
  };

  const replaceEmployee = async (
    registrationId: string,
    newEmployeeId: string,
  ) => {
    console.log(
      "replaceEmployee called - OldReg:",
      registrationId,
      " NewEmp:",
      newEmployeeId,
    );
  };

  const updateWaitlistPosition = async (
    waitlistId: number,
    newPosition: number,
  ) => {
    if (!eventSessionUuid) return;

    setIsChangingPosition(true);
    try {
      await dispatch(
        updateWaitlistPositionAction(
          {
            uuid: eventSessionUuid,
            enrollmentId: waitlistId,
            newPosition: newPosition,
          },
          () =>
            dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid })),
        ),
      );

      if (errorUpdatePosition) {
        throw new Error(errorUpdatePosition);
      }
    } catch (error) {
      console.error("Error updating position:", error);
      throw error;
    } finally {
      setIsChangingPosition(false);
    }
  };

  const moveWaitlistItemToTop = async (waitlistId: number) => {
    if (!eventSessionUuid) return;

    setIsChangingPosition(true);
    try {
      await dispatch(
        updateWaitlistPositionAction(
          {
            uuid: eventSessionUuid,
            enrollmentId: waitlistId,
            newPosition: 1,
          },
          () =>
            dispatch(fetchEventWaitlistItemsAction({ uuid: eventSessionUuid })),
        ),
      );

      if (errorUpdatePosition) {
        throw new Error(errorUpdatePosition);
      }
    } catch (error) {
      console.error("Error moving to top:", error);
      throw error;
    } finally {
      setIsChangingPosition(false);
    }
  };

  const updateAttendance = async (
    attendanceId: number,
    data: UpdateEventSessionAttendanceRequest,
  ) => {
    // Optimistic updates — no refetch, the local state is the truth
    if (data.status) {
      dispatch(
        updateDayStatusOptimistically({
          attendanceId,
          newStatus: data.status,
        }),
      );
    }

    if (data.notes !== undefined) {
      dispatch(
        updateDayNotesOptimistically({
          attendanceId,
          notes: data.notes ?? null,
        }),
      );
    }

    try {
      await dispatch(updateEventSessionAttendanceAction(attendanceId, data));
    } catch {
      // Revert on error by refetching
      const sessionUuid = eventSessionUuid || waitlistDetails?.uuid;
      if (sessionUuid) {
        dispatch(
          fetchAttendanceGridAction({
            uuid: sessionUuid,
            status: selectedAttendanceStatus || undefined,
          }),
        );
      }
      toast({
        title: "Error",
        description: "Failed to update attendance record",
        variant: "destructive",
      });
    }
  };

  const bulkUpdateAttendanceStatus = async (
    attendanceIds: number[],
    status: string,
  ) => {
    // Optimistic update
    dispatch(
      bulkUpdateDayStatusesOptimistically({
        attendanceIds,
        newStatus: status,
      }),
    );

    try {
      await dispatch(
        bulkUpdateEventSessionAttendanceAction(
          { attendanceIds, status },
          async () => {
            toast({
              title: "Success",
              description: `${attendanceIds.length} attendance records updated`,
            });
          },
        ),
      );
    } catch {
      // Revert on error by refetching
      const sessionUuid = eventSessionUuid || waitlistDetails?.uuid;
      if (sessionUuid) {
        dispatch(
          fetchAttendanceGridAction({
            uuid: sessionUuid,
            status: selectedAttendanceStatus || undefined,
          }),
        );
      }
      toast({
        title: "Error",
        description: "Failed to update attendance records",
        variant: "destructive",
      });
    }
  };

  const finalizeSession = async () => {
    if (!eventSessionUuid) {
      toast({
        title: "Error",
        description: "No event session found to finalize.",
        variant: "destructive",
      });
      return;
    }

    await dispatch(
      finalizeEventSessionAction({ uuid: eventSessionUuid }, () => {
        showNotification(
          "Success!",
          "The event session has been successfully finalized.",
          "success",
        );
        setShowFinalizeSessionDialog(false);
        refreshData();
      }),
    );
  };

  const handleExportAttendanceCsv = async () => {
    const sessionUuid = eventSessionUuid || waitlistDetails?.uuid;
    if (!sessionUuid) {
      toast({
        title: "Error",
        description: "No event session found to export attendance data.",
        variant: "destructive",
      });
      return;
    }

    try {
      await dispatch(
        exportEventAttendanceCsvAction({ uuid: sessionUuid }, (blob: Blob) => {
          const fileName = `event_session_${sessionUuid}_attendance_${new Date().toISOString().split("T")[0]}.csv`;
          downloadFile(blob, fileName);
          toast({
            title: "Export completed",
            description: "Attendance data has been exported successfully.",
          });
        }),
      );
    } catch (error) {
      console.error("Error exporting attendance CSV:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to export attendance data.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy h:mm a");
    } catch {
      return "Invalid date";
    }
  };

  const registeredUsers: RegisteredUser[] = enrollments;
  const waitlist: WaitlistEntry[] = waitlistItems;

  return {
    activeTab,
    isProcessing,
    isRefreshing,
    isMovingToWaitlist,
    isReplacing,
    isRegistering,
    isChangingPosition,
    waitlist,
    registeredUsers,
    waitlistDetails,
    showRemoveDialog,
    showMoveToWaitlistDialog,
    showReplaceDialog,
    showRegisterDialog,
    showFinalizeSessionDialog,
    selectedUser,
    selectedRegistration,
    attendances,
    loadingAttendances,
    attendanceGridItems,
    attendanceGridSessionDays,
    loadingAttendanceGrid,
    loadingEnrollments,
    loadingWaitlistItems,
    selectedAttendanceStatus,
    setSelectedAttendanceStatus: setSelectedAttendanceStatusValue,
    handleTabChange,
    refreshData,
    removeFromWaitlist,
    moveToWaitlist,
    registerFromWaitlist,
    moveFromEnrollmentToWaitlist,
    replaceEmployee,
    updateWaitlistPosition,
    moveWaitlistItemToTop,
    formatDate,
    setShowRemoveDialog,
    setShowMoveToWaitlistDialog,
    setShowReplaceDialog,
    setShowRegisterDialog,
    setShowFinalizeSessionDialog,
    setSelectedRegistration,
    setSelectedUser,
    handleRegisterClick: (user: WaitlistEntry) => {
      setSelectedUser(user);
      setShowRegisterDialog(true);
    },
    handleRemoveClick: (user: WaitlistEntry) => {
      setSelectedUser(user);
      setShowRemoveDialog(true);
    },
    handleReplaceClick: (registration: RegisteredUser) => {
      setSelectedRegistration(registration);
      setShowReplaceDialog(true);
    },
    handleMoveToWaitlistClick: (registration: RegisteredUser) => {
      setSelectedRegistration(registration);
      setShowMoveToWaitlistDialog(true);
    },
    updateAttendance,
    bulkUpdateAttendanceStatus,
    handleExportAttendanceCsv,
    finalizeSession,
    isUpdatingAttendance: loadingUpdateAttendance,
    updateAttendanceError: errorUpdateAttendance,
    isExportingCsv: loadingExportCsv,
  };
}
